import React from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { ChartContainer, ChartTooltip, ChartTooltipContent } from "@/components/ui/chart";
import { LineChart, Line, XAxis, YAxis, CartesianGrid, ResponsiveContainer } from "recharts";
import { useRealtimeActivity } from "@/hooks/dashboard/useRealtimeActivity";
import { Users } from "lucide-react";

interface UserActivityChartProps {
  timeRange: string;
  refreshInterval: number;
}

const UserActivityChart: React.FC<UserActivityChartProps> = ({
  timeRange,
  refreshInterval
}) => {
  const { activityData, activityStats, isLoading } = useRealtimeActivity(timeRange, refreshInterval);

  const chartConfig = {
    activeUsers: {
      label: "Actieve Gebruikers",
      color: "hsl(var(--primary))",
    },
    logins: {
      label: "Inloggen",
      color: "hsl(var(--secondary))",
    },
  };

  if (isLoading) {
    return (
      <Card>
        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
          <div>
            <CardTitle>Gebruiker Activiteit</CardTitle>
            <CardDescription>Real-time gebruikersstatistieken</CardDescription>
          </div>
          <Users className="h-4 w-4 text-muted-foreground animate-pulse" />
        </CardHeader>
        <CardContent>
          <div className="h-[300px] flex items-center justify-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
        <div>
          <CardTitle>Gebruiker Activiteit</CardTitle>
          <CardDescription>
            {activityStats.currentActiveUsers} gebruikers online
          </CardDescription>
        </div>
        <Users className="h-4 w-4 text-green-500 animate-pulse" />
      </CardHeader>
      <CardContent>
        {/* Quick Stats */}
        <div className="grid grid-cols-3 gap-4 mb-4">
          <div className="text-center p-2 bg-muted rounded">
            <div className="text-lg font-bold">{activityStats.currentActiveUsers}</div>
            <div className="text-xs text-muted-foreground">Online Nu</div>
          </div>
          <div className="text-center p-2 bg-muted rounded">
            <div className="text-lg font-bold">{activityStats.totalLoginsToday}</div>
            <div className="text-xs text-muted-foreground">Inloggen Vandaag</div>
          </div>
          <div className="text-center p-2 bg-muted rounded">
            <div className="text-lg font-bold">{activityStats.peakUsers}</div>
            <div className="text-xs text-muted-foreground">Piek Gebruikers</div>
          </div>
        </div>

        <ChartContainer config={chartConfig} className="h-[200px]">
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={activityData}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis 
                dataKey="time" 
                fontSize={12}
                tickLine={false}
                axisLine={false}
              />
              <YAxis 
                fontSize={12}
                tickLine={false}
                axisLine={false}
              />
              <ChartTooltip content={<ChartTooltipContent />} />
              <Line
                type="monotone"
                dataKey="activeUsers"
                stroke={chartConfig.activeUsers.color}
                strokeWidth={2}
                dot={{ fill: chartConfig.activeUsers.color, strokeWidth: 2, r: 3 }}
                activeDot={{ r: 5 }}
              />
              <Line
                type="monotone"
                dataKey="logins"
                stroke={chartConfig.logins.color}
                strokeWidth={2}
                strokeDasharray="5 5"
                dot={{ fill: chartConfig.logins.color, strokeWidth: 2, r: 3 }}
              />
            </LineChart>
          </ResponsiveContainer>
        </ChartContainer>

        {/* Activity Summary */}
        <div className="mt-4 pt-4 border-t text-sm text-muted-foreground">
          <div className="flex justify-between">
            <span>Gem. sessietijd: {activityStats.averageSessionTime} min</span>
            <span>
              Piek activiteit: {activityData.length > 0 
                ? activityData.reduce((max, curr) => 
                    curr.activeUsers > max.activeUsers ? curr : max
                  ).time
                : "Geen data"}
            </span>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

export default UserActivityChart;